import { Controller } from '@hotwired/stimulus'

// Ростер матча по командам (shared_match/show.slim): игрок матча
// перетаскивает карточку на другое место. На занятое — обмен, на свободное —
// переезд. Pointer events, а не HTML5 drag & drop: тот не работает пальцем
// на телефоне.
//
// Карточки переставляются сразу, запрос уходит следом
// (SharedMatchController#move_player). Если сервер отказал (в том числе
// потому, что состав уже поменял другой игрок), страница перезагружается
// и показывает настоящий состав.
export default class MatchTeamsController extends Controller {
  static targets = ['slot']
  // roster — метка состава, который сейчас на экране (UserMatch#roster_version):
  // сервер отклонит перестановку, если состав уже поменял кто-то другой.
  static values = { url: String, enabled: Boolean, roster: String }

  // Сдвиг, после которого нажатие считается перетаскиванием, а не кликом.
  static DRAG_THRESHOLD = 5

  connect() {
    this.onMove = this.move.bind(this)
    this.onEnd = this.end.bind(this)
  }

  disconnect() {
    this.cleanup()
  }

  start(event) {
    if (!this.enabledValue || this.busy || event.button !== 0) return
    // Крестик организатора (убрать игрока) — кнопка, а не ручка: захват
    // указателя карточкой забрал бы у неё клик.
    if (event.target.closest('[data-no-drag]')) return
    // Пальцем — только за ручку: остальная карточка оставлена прокрутке
    // страницы (touch-none стоит лишь на ручке, см. shared_match/show.slim).
    if (
      event.pointerType !== 'mouse' &&
      !event.target.closest('[data-drag-handle]')
    )
      return

    this.card = event.currentTarget
    this.origin = { x: event.clientX, y: event.clientY }
    this.dragging = false
    this.card.setPointerCapture(event.pointerId)
    this.card.addEventListener('pointermove', this.onMove)
    this.card.addEventListener('pointerup', this.onEnd)
    this.card.addEventListener('pointercancel', this.onEnd)
  }

  move(event) {
    const dx = event.clientX - this.origin.x
    const dy = event.clientY - this.origin.y

    if (!this.dragging) {
      if (Math.hypot(dx, dy) < MatchTeamsController.DRAG_THRESHOLD) return
      this.dragging = true
      this.card.classList.add(
        'relative',
        'z-10',
        'shadow-lg',
        'cursor-grabbing'
      )
    }

    this.card.style.transform = `translate(${dx}px, ${dy}px)`
    this.highlight(this.slotAt(event.clientX, event.clientY))
  }

  end(event) {
    const target =
      this.dragging && event.type === 'pointerup'
        ? this.slotAt(event.clientX, event.clientY)
        : null
    const source = this.card.closest('[data-match-teams-target="slot"]')
    const card = this.card
    this.cleanup()

    if (target && target !== source) this.swap(source, target, card)
  }

  cleanup() {
    if (!this.card) return

    this.card.removeEventListener('pointermove', this.onMove)
    this.card.removeEventListener('pointerup', this.onEnd)
    this.card.removeEventListener('pointercancel', this.onEnd)
    this.card.style.transform = ''
    this.card.classList.remove(
      'relative',
      'z-10',
      'shadow-lg',
      'cursor-grabbing'
    )
    this.highlight(null)
    this.card = null
  }

  // Слот ищем по координатам, а не через elementFromPoint: под курсором
  // всегда сама перетаскиваемая карточка.
  slotAt(x, y) {
    return this.slotTargets.find((slot) => {
      const rect = slot.getBoundingClientRect()
      return (
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      )
    })
  }

  highlight(slot) {
    this.slotTargets.forEach((element) => {
      element.classList.toggle('ring-2', element === slot)
      element.classList.toggle('ring-border-blue', element === slot)
    })
  }

  // Анонс в кнопках шеринга собран сервером со старым составом — подменяем
  // на новый из ответа, иначе «Copy link» и WhatsApp до перезагрузки
  // отправляли бы прежний порядок игроков. У начавшегося матча кнопок нет.
  updateShare({ share_text: shareText, whatsapp_text: whatsappText }) {
    const share = document.getElementById('match-share')
    if (!share) return

    share.dataset.nativeShareTextValue = shareText
    document.getElementById('match-share-copy').dataset.copyTextValue =
      shareText
    document.getElementById('match-share-whatsapp').href =
      `https://wa.me/?text=${encodeURIComponent(whatsappText)}`
  }

  // Новая метка состава — для следующей перестановки и для формы счёта
  // (SharedMatchController#update_result отклонит счёт для устаревшего
  // состава): после своей же перестановки сохранение иначе отказало бы.
  updateRoster({ roster_version: rosterVersion }) {
    this.rosterValue = rosterVersion
    const field = document.getElementById('match-result-roster')
    if (field) field.value = rosterVersion
  }

  async swap(source, target, card) {
    const other = target.firstElementChild
    source.replaceChildren(other)
    target.replaceChildren(card)

    this.busy = true
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({
          // eslint-disable-next-line camelcase -- имя параметра Rails-контроллера
          player_id: card.dataset.playerId,
          position: target.dataset.position,
          roster: this.rosterValue
        })
      })
      if (!response.ok) throw new Error(`move failed: ${response.status}`)
      const data = await response.json()
      this.updateShare(data)
      this.updateRoster(data)
    } catch {
      window.location.reload()
    } finally {
      this.busy = false
    }
  }
}
